import { useCallback, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';
import { quickEditApi } from '@client/api/waterlily';
import { fetchClientByClientIdRequest } from '@client/model/clientModel';
import { fetchTypeformPayloadByClientIdRequest } from '@client/model/typeformPayloadModel';
import { fetchClientsForAdvisorByAdvisorIdRequest } from '@client/model/clientsModel';
import { selectSessionAdvisor } from '@client/model/selectors';

interface UseQuickEditIntakeOptions {
  clientId: string;
  onSaveSuccess?: () => void;
  successMessage?: string;
}

export function useQuickEditIntake({
  clientId,
  onSaveSuccess,
  successMessage = 'Intake form updated successfully.',
}: UseQuickEditIntakeOptions) {
  const dispatch = useDispatch();
  const advisor = useSelector(selectSessionAdvisor);
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [isRefreshing, setIsRefreshing] = useState(false);

  const mutation = useMutation({
    mutationFn: (fields: Record<string, string | number | null>) =>
      quickEditApi.save(clientId, fields),
    onSuccess: () => {
      setIsRefreshing(true);
      dispatch(fetchClientByClientIdRequest(clientId));
      dispatch(fetchTypeformPayloadByClientIdRequest({ clientId }));
      if (advisor?.advisorId) {
        dispatch(fetchClientsForAdvisorByAdvisorIdRequest({ advisorId: advisor.advisorId, isInvalidation: true }));
      }
      toast.success(successMessage);
      setMode('view');
      onSaveSuccess?.();
    },
    onError: (error: unknown) => {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to update intake form.';
      toast.error(errorMessage);
    },
  });

  const clearRefreshing = useCallback(() => {
    setIsRefreshing(false);
  }, []);

  return {
    mode,
    setMode,
    save: mutation.mutate,
    isPending: mutation.isPending,
    isRefreshing,
    clearRefreshing,
    isError: mutation.isError,
  };
}
